/**
 * 🔴 **MUI 主题 —— 值只从生成的 token 产物来**（架构 §10.2 · 铁律 10）
 *
 * ## 这个文件为什么这么"薄"
 *
 * 因为设计令牌的**唯一真相源**是 `docs/03-UX与UI设计/assets/tokens.css`，而 MUI 需要结构化值。
 * 两者之间的桥**由脚本生成**（`tools/gen-tokens.ts` → `tokens.generated.ts`），
 * 本文件只做**映射**（令牌名 → MUI 的槽位），**不写任何字面量色值/尺寸**。
 *
 * 🔴 **一旦这里出现 `#xxxxxx` 或 `14px` 这样的字面量，第二份真相就诞生了** —— 那正是 v1 病灶②
 * （`app.wxss` + `theme.ts` 三处真相）的复现路径。要改颜色/字号，**只改 `tokens.css`** 再重跑生成器。
 *
 * ## 🔴 一条硬指标（架构 §15.1-8 的回看触发条件）
 *
 * > 若 `tokens.css` ↔ MUI theme 的映射需要 **>20 处** `sx` / `styleOverrides` hack，
 * > 或出现**第三份色值** → 回看「自建薄组件层 + headless」。
 *
 * 本文件当前 `styleOverrides` 用在**必要的**地方（按钮 textTransform、Paper 圆角等 MUI 默认与设计规范冲突处），
 * 逐条都在下方标了原因。**加新的 override 前先数一数**：总数逼近 20 就该回看了。
 *
 * ## ⚠️ 暗色模式怎么处理（v6 的 `colorSchemes`）
 *
 * `TOKENS_DARK_OVERRIDES` 是**覆盖表**（只有 41 项，不是全表）⇒ 必须与亮色**合并**再建主题，
 * 否则暗色下缺键的槽位会落到 MUI 默认色（页面会"部分变亮"）。合并函数是 `resolveTokens('dark')`。
 */

import { createTheme, type ThemeOptions } from '@mui/material/styles';
import type { ThemeValue } from '@jiaying/shared';
import { TOKENS_DARK_OVERRIDES, TOKENS_LIGHT } from './tokens.generated.js';

/** 令牌表（键 = 去掉前导 `--` 的原名，值 = 已解析真值） */
export type TokenTable = Record<string, string>;

/**
 * 🔴 **三态 → 明/暗的唯一解析处**（`AC-N3.1` 的「跟随系统」半边）。
 *
 * `'system'` 必须解析成**当下**的系统外观 —— 而"当下是什么"只有客户端知道
 * （`prefers-color-scheme` 是浏览器能力）。这也是为什么本函数在**端侧**而不是共享层：
 * 服务端只存偏好，它无权也无需知道用户的系统外观。
 *
 * 🔴 **纯函数**（系统外观作为**入参**传入）—— `matchMedia` 的读取留在调用方，
 * 这样"跟随系统"的判定可被静态断言（否则会变成"只有真浏览器能测"的逻辑）。
 *
 * @param pref 用户偏好三态（`user_preferences.theme`）
 * @param systemPrefersDark 当下系统是否偏好深色（`matchMedia('(prefers-color-scheme: dark)')`）
 */
export function resolveThemeMode(pref: ThemeValue, systemPrefersDark: boolean): 'light' | 'dark' {
  if (pref === 'system') return systemPrefersDark ? 'dark' : 'light';
  return pref;
}

/** 🔴 取某个主题模式下的**完整**令牌表（暗色 = 亮色基底 + 覆盖，见文件头说明） */
export function resolveTokens(mode: 'light' | 'dark'): TokenTable {
  if (mode === 'light') return { ...TOKENS_LIGHT };
  return { ...TOKENS_LIGHT, ...TOKENS_DARK_OVERRIDES };
}

/**
 * 🆕 **顶栏「切换主题」点一下的下一档**（批次 `94` · 纯函数，可成对断言）。
 *
 * ## 🔴 为什么取反要基于**解析后**的明/暗，而不是 `pref` 本身
 *
 * 用户当前可能是 `'system'`（系统深色 ⇒ 他看到深色）。此时点切换，他要的是
 * **"我就要浅色"** —— 落成 `'light'`；若只在 `system` / `light` 之间猜，
 * 或者在两个显式值之间来回，都会出现"点了之后仍是深色"（等于没点）。
 *
 * ## 🔴 返回值**恒为显式值**（永不为 `'system'`）
 *
 * 落成 `'system'` 会让下一帧被系统外观拉回去 —— 那正是负责人 2026-10-21
 * 报的"怎么点都在暗色"的其中一半（另一半是 `app/useThemeSync.ts` 的回灌，见该文件头）。
 */
export function nextExplicitTheme(pref: ThemeValue, systemPrefersDark: boolean): 'light' | 'dark' {
  return resolveThemeMode(pref, systemPrefersDark) === 'dark' ? 'light' : 'dark';
}

/** 读令牌（缺失时**直接抛**，而不是悄悄给个兜底色 —— 静默兜底就是第二份真相的开端） */
function t(table: TokenTable, name: string): string {
  const value = table[name];
  if (value === undefined) {
    throw new Error(`令牌 ${name} 不存在 —— 检查 docs/03-UX与UI设计/assets/tokens.css，并重跑 tools/gen-tokens.ts`);
  }
  return value;
}

/**
 * 令牌里的 px 数值（MUI 的 `spacing` / `borderRadius` 要裸数字，不要 `'14px'`）。
 *
 * 🔴 `Number.parseFloat` 在本项目被 lint 限制在定点白名单内（规格 §22.1：金额禁浮点）。
 * **这里不是金额场景** —— 解析的是**设计尺寸**（`--r-md: 10px` / `--sp-1: 4px`），
 * 结果只喂给 MUI 的样式数值，不参与金额计算、不进库、不做加减。
 * ⇒ 按规则提供的逃生舱显式豁免，**并写明理由**（不写理由的豁免就是纪律失效的开始）。
 */
function px(table: TokenTable, name: string): number {
  const raw = t(table, name);
  // eslint-disable-next-line no-restricted-syntax -- 设计尺寸解析，非金额（见上方说明）
  const n = Number.parseFloat(raw.replace('px', ''));
  if (Number.isNaN(n)) throw new Error(`令牌 ${name} 不是 px 值：${raw}`);
  return n;
}

/**
 * 由令牌构造 MUI 主题。
 *
 * @param mode `'light'` / `'dark'` —— 由 `ThemeProvider` 按用户偏好传入
 */
export function buildTheme(mode: 'light' | 'dark') {
  const k = resolveTokens(mode);

  const options: ThemeOptions = {
    palette: {
      mode,
      background: {
        default: t(k, 'c-bg'),
        paper: t(k, 'c-surface'),
      },
      text: {
        // ⚠️ 只映射 MUI 认的两个槽位；设计的 `--c-text-2/-3` 通过 `theme.jiaying` 下发
        primary: t(k, 'c-text'),
        secondary: t(k, 'c-text-2'),
      },
      divider: t(k, 'c-divider'),
      primary: {
        main: t(k, 'c-brand'),
        // 🔴 设计规范：主按钮**填充色与文字形态色是两个令牌**（`--c-brand-fill` ≠ `--c-brand`），
        //    后者单独用会掉到 AA 以下（`tokens.css` 注释里记了实测 4.93:1 的来历）
        dark: t(k, 'c-brand-fill'),
        contrastText: t(k, 'c-text-invert'),
      },
      // 🔴 财务语义色是**中国约定**：红涨绿跌（与欧美相反）—— 见规格 §22 / O 域 PRD
      success: { main: t(k, 'c-up') },
      error: { main: t(k, 'c-down') },
      warning: { main: t(k, 'c-alert') },
      info: { main: t(k, 'c-brand') },
    },
    shape: { borderRadius: px(k, 'r-md') },
    // 🔴 4pt 间距基（`--sp-1` = 4px）⇒ MUI 的 `spacing(1)` 与设计规范同义
    spacing: px(k, 'sp-1'),
    typography: {
      fontFamily: t(k, 'font-sans'),
      // 金额一律用等宽数字字体（对齐是财务界面的基本要求）
      // eslint-disable-next-line no-restricted-syntax -- 同上：解析字号（设计尺寸），非金额
      fontSize: Number.parseFloat(t(k, 'fs-14').replace('px', '')),
      button: { textTransform: 'none' }, // 设计规范不用全大写
    },
    /**
     * 🔴 **本项目的扩展槽** —— 设计令牌里 MUI 没有对应槽位的部分放这里，
     *   由组件经 `useTheme().jiaying.*` 取用（**不再各页写 var() 字面量**）。
     */
    jiaying: {
      tokens: k,
      text: { secondary: t(k, 'c-text-2'), tertiary: t(k, 'c-text-3') },
      surface: { sunk: t(k, 'c-surface-sunk'), raise: t(k, 'c-surface-raise') },
      border: { base: t(k, 'c-border'), strong: t(k, 'c-border-strong') },
      /** 财务语义（红涨绿跌）的**背景**变体 */
      money: {
        up: t(k, 'c-up'),
        down: t(k, 'c-down'),
        upBg: t(k, 'c-up-bg'),
        downBg: t(k, 'c-down-bg'),
      },
      /** 分类色板（图表用「同一分类跨图表同色」） */
      chart: [1, 2, 3, 4, 5, 6, 7, 8].map((i) => t(k, `cat-${i}`)),
      font: { display: t(k, 'font-display'), num: t(k, 'font-num') },
      fontSize: {
        x11: t(k, 'fs-11'),
        x12: t(k, 'fs-12'),
        x13: t(k, 'fs-13'),
        x15: t(k, 'fs-15'),
        x16: t(k, 'fs-16'),
        x18: t(k, 'fs-18'),
        x20: t(k, 'fs-20'),
        x24: t(k, 'fs-24'),
        x28: t(k, 'fs-28'),
        x34: t(k, 'fs-34'),
        hero: t(k, 'fs-hero'),
      },
      radius: {
        xs: t(k, 'r-xs'),
        sm: t(k, 'r-sm'),
        lg: t(k, 'r-lg'),
        xl: t(k, 'r-xl'),
        full: t(k, 'r-full'),
      },
      shadow: {
        s1: t(k, 'sh-1'),
        s2: t(k, 'sh-2'),
        s3: t(k, 'sh-3'),
        s4: t(k, 'sh-4'),
        focus: t(k, 'sh-focus'),
      },
      easing: { outExpo: t(k, 'e-out-expo'), outQuart: t(k, 'e-out-quart'), inOut: t(k, 'e-in-out') },
      /**
       * 🔴 **Web 骨架尺寸**（O 域 `UI工程移交文档_Web端` §5.3 / §9：侧栏 228px、折叠 64px、
       * 顶栏 58px、内容上限 1320px、页边距 `clamp(16px, 2.2vw, 32px)`）。
       *
       * ⚠️ 这五项 **只对 Web 端成立** —— 小程序是「无侧栏、改底部 tab bar」（同文档 §9 端别表）
       * ⇒ 属 `web-layout-only` 令牌：`gen-tokens` 的 `rpx` 表里标注为不产出 rpx（不参与小程序换算）。
       */
      layout: {
        sidebarW: t(k, 'sidebar-w'),
        sidebarWc: t(k, 'sidebar-wc'),
        topbarH: t(k, 'topbar-h'),
        maxw: t(k, 'maxw'),
        padPage: t(k, 'pad-page'),
      },
    },
    components: {
      // MUI 默认的 elevation 阴影与设计阴影不同源 ⇒ 关掉，统一走 `jiaying.shadow`
      // ⚠️ 槽位名**必须带 `Mui` 前缀**（`MuiPaper` 而不是 `Paper`）—— 写错时 MUI 的类型会报
      //    "Object literal may only specify known properties"，但**运行时不报错**（只是这些覆盖静默失效）。
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: { root: { backgroundImage: 'none' } },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: { root: { backgroundImage: 'none' } },
      },
      MuiAppBar: { defaultProps: { elevation: 0, color: 'transparent' } },
      MuiButton: { defaultProps: { disableElevation: true } },
    },
  };

  return createTheme(options);
}

/**
 * `theme.jiaying` 的类型声明（MUI v6 的模块增强）。
 *
 * 🔴 不声明的话，`useTheme().jiaying` 是 `any` —— 那就等于把设计令牌的访问变成**无类型字符串索引**，
 * 拼错一个名字不会有任何提示（静默 `undefined` ⇒ 组件样式静默失效）。
 */
declare module '@mui/material/styles' {
  interface Theme {
    jiaying: {
      tokens: TokenTable;
      text: { secondary: string; tertiary: string };
      surface: { sunk: string; raise: string };
      border: { base: string; strong: string };
      money: { up: string; down: string; upBg: string; downBg: string };
      chart: string[];
      font: { display: string; num: string };
      fontSize: Record<string, string>;
      radius: Record<string, string>;
      shadow: Record<string, string>;
      easing: Record<string, string>;
      /** Web 骨架尺寸（小程序不消费，见上方 `layout` 说明） */
      layout: { sidebarW: string; sidebarWc: string; topbarH: string; maxw: string; padPage: string };
    };
  }
  interface ThemeOptions {
    jiaying?: Theme['jiaying'];
  }
}
